<template>
  <div>
    <div class="container">
      <LayoutColumns>
        <template v-slot:left />
        <template v-slot:main>
          <div v-if="isMm()" class="text-center">
            <div class="section-label mb-8">Søg i arkivet</div>
          </div>
          <div class="relative flex mb-0">
            <input
              ref="search"
              v-model="searchWords"
              type="text"
              class="relative outline-none w-full px-5 transition-all duration-300 body-m py-3"
              :class="
                isMm()
                  ? 'rounded-md border'
                  : 'rounded-full text-blue bg-blue bg-opacity-5'
              "
              :style="{ backgroundColor: isMm() ? '#fff' : undefined }"
              :placeholder="$t('Search')"
              @keyup.enter="refresh"
            />
            <SearchIcon
              class="cursor-pointer top-1/2 transform -translate-y-1/2 p-3 w-12 h-12 mr-2 absolute right-0 transition-all duration-200 fill-current"
              :class="isMm() ? 'text-black' : 'text-blue'"
              @click="refresh"
            />
          </div>
          <div class="flex items-center">
            <FormCheckbox
              id="searchOneYear"
              v-model="searchOneYear"
              :checked="searchOneYear"
            />
            <span class="body-s">{{ $t('SearchOneYear') }}</span>
          </div>
        </template>
        <template v-slot:right />
      </LayoutColumns>
    </div>

    <template v-if="isMm()">
      <div class="container">
        <LayoutColumns>
          <template v-slot:left />
          <template v-slot:main>
            <ul>
              <li v-for="(result, index) in articles" :key="index">
                <ArticleTeaser
                  :article="result"
                  :index="index + 2"
                  :always-image-small="true"
                />
                <CommonDivider class="my-3 md:my-5" />
              </li>
            </ul>
            <div
              v-if="articles?.length ?? 0 % itemsPerPage === 0"
              class="flex justify-center"
            >
              <CommonButton @click.native="showMore('article')">
                {{ $t('ShowMoreSearchResults') }}
              </CommonButton>
            </div>
          </template>
          <template v-slot:right />
        </LayoutColumns>
      </div>
      <CommonDivider />
      <CommonSectionLabel
        title="Få hele sammenhængen i et aktuelt tema"
        subtitle="Find de seneste temaer her"
        link="ugenstemaer"
      />
      <ListThemes />
      <CommonDivider />
      <CommonSectionLabel
        title="Mandag Morgen er også et ugebrev"
        subtitle="Hent de seneste ugebreve her"
        link="ugebreve"
      />
      <ListMagazines :count="15" />
      <AdsConceptAd id="mm-lb2" />
    </template>

    <template v-else>
      <div class="container">
        <CommonSectionLabel
          id="section-0"
          :title="'NewsAndDebate'"
          class="opacity-35"
          :black="true"
        />
        <LayoutColumns>
          <template v-slot:left>
            <CommonDivider class="hidden md:block" />
            <CommonQuickMenu
              v-if="CommonQuickMenuItems.length > 0"
              :items="CommonQuickMenuItems"
              class="hidden md:block"
            />
          </template>
          <template v-slot:main>
            <CommonDivider />
            <ul>
              <li v-for="(result, index) in articles" :key="index">
                <ArticleTeaser
                  :article="result"
                  :index="index + 2"
                  :show-publishing-date="true"
                />
                <CommonDivider class="my-3 md:my-5" />
              </li>
            </ul>
            <div
              v-if="articles?.length ?? 0 % itemsPerPage === 0"
              class="flex justify-center"
            >
              <CommonButton @click.native="showMore('article')">
                {{ $t('ShowMoreSearchResults') }}
              </CommonButton>
            </div>

            <template v-if="persons && persons.length > 0">
              <CommonSectionLabel
                id="section-1"
                :title="'Card'"
                class="opacity-35"
                :black="true"
              />
              <ul>
                <li v-for="(person, index) in persons" :key="index">
                  <PersonTeaser :person="person" :index="index" />
                </li>
              </ul>
              <div
                v-if="persons?.length ?? 0 % itemsPerPage === 0"
                class="flex justify-center"
              >
                <CommonButton @click.native="showMore('person')">
                  {{ $t('ShowMoreSearchResults') }}
                </CommonButton>
              </div>
            </template>

            <template v-if="qa && qa.length > 0">
              <CommonSectionLabel
                id="section-2"
                :title="'MinisterQA'"
                class="opacity-35"
                :black="true"
              />
              <CommonDivider />
              <ul>
                <li v-for="(result, index) in qa" :key="index">
                  <QATeaser :qa-article="result" placement="main" />
                  <CommonDivider />
                </li>
              </ul>
              <div
                v-if="qa?.length ?? 0 % itemsPerPage === 0"
                class="flex justify-center"
              >
                <CommonButton @click.native="showMore('qa')">
                  {{ $t('ShowMoreSearchResults') }}
                </CommonButton>
              </div>
            </template>
          </template>
          <template v-slot:right>
            <ListJobs />
          </template>
        </LayoutColumns>
      </div>
      <CommonSectionLabel
        title="Papers"
        link="Routes.Papers"
        link-label="ShowAllPapers"
      />
      <ListPapersMM v-if="isMm()" type="slider" />
      <ListPapers v-else />
    </template>
  </div>
</template>

<script setup lang="ts">
import SearchIcon from '~/assets/icons/search.svg?component'
import type { ContentArticle, Person } from '~/typesManual/content_api/article'
import type { QADto } from '~/typesAuto/apicore/v1'

type searchTypes = 'article' | 'person' | 'qa'

const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()
const route = useRoute()

const indexStore = useIndexStore()

const props = defineProps({
  itemsPerPage: {
    type: Number,
    default: 8,
  },
})

let searchOneYear = false
const { itemsPerPage } = toRefs(props)
const pages = ref<Record<searchTypes, number>>({
  ['article']: 0,
  ['person']: 0,
  ['qa']: 0,
})
const searchWords = ref(useRoute().params.words)

useHead({
  title: nuxtApp.$t('SearchOnBrand', {
    brand: config.public.site.name,
    words: searchWords.value,
  }),
})

type ObjectType<T> = T extends 'article'
  ? ContentArticle[]
  : T extends 'person'
  ? Person[]
  : T extends 'qa'
  ? QADto[]
  : never

const search = async <T extends searchTypes>(
  type: T
): Promise<ObjectType<T>> => {
  return await nuxtApp.$api.content.search({
    words: searchWords.value,
    paper: indexStore.currentPaper?.RecordId
      ? indexStore.currentPaper.RecordId
      : 1,
    page: 0,
    pageSize: itemsPerPage.value * (1 + pages.value[type]),
    searchOneYear,
    type,
  })
}

const { data: articles } = await useAsyncData('article', () =>
  search('article')
)
// Be lazy with secondary search types to save on requests
const { data: persons } = useLazyAsyncData('person', () => search('person'))
const { data: qa } = useLazyAsyncData('qa', () => search('qa'))

const CommonQuickMenuItems = computed(() => {
  const items = [
    {
      title: nuxtApp.$t('NewsAndDebate'),
      target: 'section-0',
    },
  ]

  if (persons.value && persons.value.length > 0) {
    items.push({
      title: nuxtApp.$t('Card'),
      target: 'section-1',
    })
  }
  if (qa.value && qa.value.length > 0) {
    items.push({
      title: nuxtApp.$t('MinisterQA'),
      target: 'section-2',
    })
  }
  return items
})

const refresh = () => {
  return navigateTo({
    name: route.name,
    params: { words: searchWords.value },
  })
}

const showMore = (type: searchTypes) => {
  pages.value[type]++
  refreshNuxtData(type)
}
</script>
